import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Barcode from "react-barcode";
import autoTable from "jspdf-autotable";

// Redux Actions
import {
  fetchLoanEmis,
  clearLoanEmis,
} from "../redux/slices/loan/loanEmiSlice";
import { fetchAllLoans } from "../redux/slices/loan/loanReportSlice";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";

import "../assets/Loan/LoanPassbook.css";

const PASSBOOK_CONFIG = {
  orientation: "portrait",
  unit: "mm",
  format: "a4",
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const formatAmount = (a) =>
  a ? `₹ ${Number(a).toLocaleString("en-IN")}` : "₹ 0";

const LoanPassbook = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();

  const passbookRef = useRef(null);
  const { emis } = useSelector((state) => state.loanEmi);
  const { loans } = useSelector((state) => state.loanReport);
  const { profile } = useSelector((state) => state.userProfile);

  const [company, setCompany] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(fetchAllLoans());
    dispatch(fetchLoanEmis(loanId));
    return () => dispatch(clearLoanEmis());
  }, [dispatch, loanId]);

  const loan = loans.find((l) => String(l.id) === String(loanId));

  // Fetch Company Details
  useEffect(() => {
    if (!profile?.superAdminId) return;
    fetch(
      `https://narrowitsolution.in/api/get-company?superAdminId=${profile.superAdminId}`,
    )
      .then((r) => r.json())
      .then((d) => {
        if (d?.logo)
          d.logoUrl = `https://narrowitsolution.in/${d.logo.replace(/^images\//, "")}`;
        if (d?.qrCodeImage)
          d.qrCodeUrl = `https://narrowitsolution.in/${d.qrCodeImage.replace(/^images\//, "")}`;
        setCompany(d);
      });
  }, [profile?.superAdminId]);

  // Fetch Customer Details
  useEffect(() => {
    if (!loan) return;
    const branchId = localStorage.getItem("userId") || 0;

    fetch(
      `https://narrowitsolution.in/api/customers-get-userId?userId=${loan.customerId}&branchId=${branchId}`,
    )
      .then((r) => r.json())
      .then((d) => setCustomer(d.data));

    fetch(
      `https://narrowitsolution.in/api/list-kyc?userId=${loan.customerId}&superAdminId=${profile?.superAdminId}&createdById=${branchId}`,
    )
      .then((r) => r.json())
      .then((d) => {
        const p = d.documents?.find(
          (x) => x.docName?.toLowerCase() === "photo",
        );
        if (p?.docImage) setPhotoUrl(`https://narrowitsolution.in/uploads/${p.docImage}`);
      });
  }, [loan, profile?.superAdminId]);

  /* ================= UTILS: PDF GENERATION ================= */
  const generatePassbookImagePDF = async () => {
    if (!passbookRef.current) return null;

    const canvas = await html2canvas(passbookRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#fff",
      logging: false, 
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.8); 
    const pdf = new jsPDF(PASSBOOK_CONFIG);

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    return pdf;
  };

  const downloadPassbookPdf = async () => {
    const pdf = await generatePassbookImagePDF();
    if (pdf) pdf.save(`Passbook_${loan?.loanAccountNumber}.pdf`);
  };

  /* ================= SHARED PRINTING UTILITY (INSTANT) ================= */
const printFromBlob = (pdf) => {
  const blobUrl = pdf.output("bloburl");

  // Iframe method ki jagah, hum direct blob URL ko target karenge
  const iframe = document.createElement("iframe");
 iframe.style.display = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

  document.body.appendChild(iframe);

 iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      window.addEventListener(
        "focus",
        () => {
          setTimeout(() => {
            if (document.body.contains(iframe))
              document.body.removeChild(iframe);
          }, 500);
        },
        { once: true },
      );
    };
};

  /* ================= 1. PASSBOOK COVER (IMAGE BASED) ================= */
const printPassbook = async () => {
  setLoading(true);
  const pdf = await generatePassbookImagePDF();
  if (pdf) printFromBlob(pdf); // Naya wala function
  setLoading(false);
};

  const handlePassbookPrint = async () => {
    setLoading(true);
    await printPassbook();
    setLoading(false);
  };


// for emi printing 
const A4_HEIGHT = 297;
const HALF_HEIGHT = A4_HEIGHT / 2;

const TOP_MARGIN = 10;
const BOTTOM_MARGIN = 6;

const ROWS_PER_HALF = 17;


const buildEmiTable = (doc) => {
  const tableHead = [[
    "Sr",
    "Date",
    "Principal",
    "Interest",
    "EMI",
    "Recv Date",
    "Staff Sign",
    "Member Sign",
  ]];

  const tableBody = emis.map((item, i) => [
    i + 1,
    formatDate(item.dueDate),
    `Rs. ${item.principalComponent}`,
    `Rs. ${item.interestComponent}`,
    `Rs. ${item.emiAmount}`,
    "",
    "",
    "",
  ]);

  let index = 0;

  while (index < tableBody.length) {

    /* 🔹 TOP HALF */
    autoTable(doc, {
      startY: TOP_MARGIN,
      margin: { left: 8, right: 8 },
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2, halign: "center" },
      headStyles: { fillColor: [43, 41, 41], textColor: 255 },
      head: tableHead,
      body: tableBody.slice(index, index + ROWS_PER_HALF),
    });

    index += ROWS_PER_HALF;
    if (index >= tableBody.length) break;

    /* 🔹 BOTTOM HALF */
    autoTable(doc, {
      startY: HALF_HEIGHT + TOP_MARGIN,
      margin: { left: 8, right: 8 },
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2, halign: "center" },
      headStyles: { fillColor: [43, 41, 41], textColor: 255 },
      head: tableHead,
      body: tableBody.slice(index, index + ROWS_PER_HALF),
    });

    index += ROWS_PER_HALF;

    const remainingRows = tableBody.length - index;

    // ✅ SMART page break
    if (remainingRows > ROWS_PER_HALF) {
      doc.addPage();
    }
  }
};

const downloadEmiPDF = () => {
  const doc = new jsPDF("p", "mm", "a4");
  buildEmiTable(doc);
  doc.save(`EMI_Schedule_${loan?.loanAccountNumber}.pdf`);
};

const printEmiPDF = () => {
  const doc = new jsPDF("p", "mm", "a4");
  buildEmiTable(doc);
  printFromBlob(doc);
};





  if (!loan)
    return <div className="p-10 text-center">Loading Loan Data...</div>;

  return (
    <div className="loan-passbook-container">
      {/* PASSBOOK COVER PAGE */}
      {/* <div ref={passbookRef} className="loan-passbook-page">
        <div className="lpb-company-div">
          <img
            className="lpb-company-logo"
            src={company?.logoUrl}
            alt="Logo"
            crossOrigin="anonymous"
          />
          <div className="lpb-company-details">
            <h2>{company?.name}</h2>
            <p>
              <strong>Office:</strong> {company?.address}
            </p>
            <p>
              <strong>CIN:</strong> {company?.cin}
            </p>
            <p>
              <strong>Contact:</strong> {company?.contact} | {company?.email}
            </p>
          </div>
          <img
            className="lpb-company-qr-code"
            src={company?.qrCodeUrl}
            alt="QR"
            crossOrigin="anonymous"
          />
        </div>

        <div className="lpb-barcode-wrapper">
          <Barcode
            value={String(loanId)}
            height={25}
            width={1.2}
            fontSize={12}
          />
          <h3 className="lpb-title">Loan Passbook</h3>
        </div>

        <div className="lpb-customer-details">
          <table className="lpb-customer-table">
            <tbody>
              <tr>
                <td>
                  <b>Customer</b>
                </td>
                <td>{loan.userName}</td>
                <td>
                  <b>ID</b>
                </td>
                <td>{loan.customerId}</td>
                <td rowSpan="4" className="lpb-customer-photo">
                  <div className="lpb-customer-scanner-frame">
                    <img
                      src={
                        photoUrl ||
                        "https://upload.wikimedia.org/wikipedia/commons/6/65/No-Image-Placeholder.svg"
                      }
                      alt="Customer"
                      crossOrigin="anonymous"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <b>Loan No</b>
                </td>
                <td>{loan.loanAccountNumber}</td>
                <td>
                  <b>Amount</b>
                </td>
                <td>{formatAmount(loan.loanAmount)}</td>
              </tr>
              <tr>
                <td>
                  <b>Total EMI</b>
                </td>
                <td>{emis.length}</td>
                <td>
                  <b>Plan</b>
                </td>
                <td>{loan.loanService}</td>
              </tr>
              <tr>
                <td>
                  <b>Address</b>
                </td>
                <td colSpan="3" style={{ fontSize: "10px" }}>
                  {customer?.address || "N/A"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="lpb-info-footer">
          <span>Customer Signature</span>
          <span>Branch Seal & Sign</span>
        </div>
      </div> */}

      <div ref={passbookRef} className="loan-passbook-page">
        <div className="lpb-grid-page">
          <div className="lpb-back-page">
             <img
            src={company?.logoUrl}
            alt="Logo"
            crossOrigin="anonymous"
          />
          <div className="lpb-back-company-details">
              <h2>{company?.name}</h2>
            <p>
              <strong>Office:</strong> {company?.address}
            </p>
            <p>
              <strong>CIN:</strong> {company?.cin}
            </p>
            <p>
              <strong>Contact:</strong> {company?.contact} | {company?.email}
            </p>
            </div>
          </div>

          <div className="lpb-front-page">
            <div className="lpb-front-company-logo-scanner">
              <img
            src={company?.logoUrl}
            alt="Logo"
            crossOrigin="anonymous"
          />
          <img
            src={company?.qrCodeUrl}
            alt="QR"
            crossOrigin="anonymous"
          />
            </div>

            <div className="lpb-front-company-details">
              <h2>{company?.name}</h2>
            <p>
              <strong>Office:</strong> {company?.address}
            </p>
            <p>
              <strong>CIN:</strong> {company?.cin}
            </p>
            <p>
              <strong>Contact:</strong> {company?.contact} | {company?.email}
            </p>
            </div>

            <div className="lpb-front-barcode-wrapper">
          <Barcode
            value={String(loanId)}
            height={25}
            width={1.2}
            fontSize={12}
          />
          <h3 className="lpb-front-title">Loan Passbook</h3>
        </div>

        <div className="lpb-front-customer-details">
         <table className="lpb-front-customer-table">
  <tbody>
    <tr>
      <td className="label">Customer</td>
      <td className="value">{loan.userName}</td>

      {/* PHOTO */}
      <td
        className="lpb-front-customer-photo"
        rowSpan={4}
        colSpan={2}
      >
        <div className="lpb-front-customer-scanner-frame">
          <img
            src={
              photoUrl ||
              "https://upload.wikimedia.org/wikipedia/commons/6/65/No-Image-Placeholder.svg"
            }
            alt="Customer"
            crossOrigin="anonymous"
          />
        </div>
      </td>
    </tr>

    <tr>
      <td className="label">Customer ID</td>
      <td className="value">{loan.customerId}</td>
    </tr>

    <tr>
      <td className="label">Loan No</td>
      <td className="value">{loan.loanAccountNumber}</td>
    </tr>

    <tr>
      <td className="label">Amount</td>
      <td className="value">{formatAmount(loan.loanAmount)}</td>
    </tr>

    <tr>
      <td className="label">Plan</td>
      <td className="value">{loan.loanService}</td>
      <td className="label">Total EMI</td>
      <td className="value">{emis.length}</td>
    </tr>

    <tr>
      <td className="label">Address</td>
      <td className="value address" colSpan={3}>
        {customer?.address || "N/A"}
      </td>
    </tr>
  </tbody>
</table>

        </div>

        <div className="lpb-front-info-footer">
          <span>Customer Signature</span>
          <span>Branch Seal & Sign</span>
        </div>

          </div>

          
        </div>
      </div>



      {/* ACTION BUTTONS */}
      <div className="lpb-action-bar">
        <button onClick={downloadPassbookPdf} className="btn-download">
          Download Cover
        </button>
        <button
          onClick={handlePassbookPrint}
          disabled={loading}
          className="btn-print"
        >
          {loading ? "Preparing..." : "Print Passbook Cover"}
        </button>
        <button onClick={downloadEmiPDF} className="btn-download-emi">
          Download EMI Table
        </button>
        <button onClick={printEmiPDF} className="btn-print-emi">
          Print EMI Table
        </button>
      </div>

      {/* PREVIEW OF EMI TABLE (WEB VIEW ONLY) */}
      <div className="loan-passbook-page lpb-auto-height mt-4">
        <h4 className="text-center mb-2">EMI Repayment Schedule</h4>
        <table className="lpb-emi-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>EMI</th>
              <th>Receiving Date</th>
              <th>Staff Sign</th>
              <th>Member Sign</th>
            </tr>
          </thead>
          <tbody>
            {emis.map((e, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{formatDate(e.dueDate)}</td>
                <td>{formatAmount(e.principalComponent)}</td>
                <td>{formatAmount(e.interestComponent)}</td>
                <td>{formatAmount(e.emiAmount)}</td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LoanPassbook;
